import { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Polyline, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Upload, Loader2, MapPin, Route, Trash2 } from "lucide-react";
import api from "@/lib/api";

// Fix default marker icons (Leaflet bundling quirk in webpack)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl:       "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl:     "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const PATH_COLORS = ["#003366", "#EA580C", "#10b981", "#7c3aed", "#dc2626", "#0891b2"];

function FitBounds({ bounds }) {
  const map = useMap();
  useEffect(() => {
    if (bounds && bounds.length >= 2) {
      map.fitBounds(bounds, { padding: [20, 20] });
    }
  }, [bounds, map]);
  return null;
}

/**
 * KmzPanel — upload + view KMZ layers (Google Earth) on a Leaflet map.
 * Props: projectId
 */
export default function KmzPanel({ projectId }) {
  const [layers, setLayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/projects/${projectId}/kmz`);
      setLayers(r.data?.items || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (projectId) load(); /* eslint-disable-next-line */ }, [projectId]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/projects/${projectId}/kmz`, fd,
                     { headers: { "Content-Type": "multipart/form-data" } });
      await load();
      setActiveIdx(0);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const active = layers[activeIdx];
  const allCoords = [];
  if (active) {
    (active.placemarks || []).forEach(pm => allCoords.push([pm.lat, pm.lon]));
    (active.paths || []).forEach(p => p.coords?.forEach(c => allCoords.push([c.lat, c.lon])));
  }
  const center = allCoords.length ? allCoords[0] : [4.6, -74.07];

  return (
    <div data-testid="kmz-panel" className="space-y-4">
      {/* Header / upload */}
      <div className="bg-white border border-slate-200 rounded-2xl p-4 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <MapPin size={16} className="text-emerald-600" />
          <div>
            <div className="text-xs font-mono uppercase tracking-wider text-slate-500">
              KMZ · Google Earth (red de fibra/datos)
            </div>
            <div className="text-sm font-medium text-slate-900">
              {layers.length} capa{layers.length === 1 ? "" : "s"} cargada{layers.length === 1 ? "" : "s"}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" accept=".kmz,.kml"
                 onChange={upload}
                 className="hidden"
                 data-testid="kmz-upload-input" />
          <button
            data-testid="kmz-upload-btn"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="text-xs font-mono uppercase tracking-wider px-3 py-1.5 bg-[#003366] text-white hover:bg-[#002244] rounded-xl inline-flex items-center gap-1.5 disabled:opacity-60">
            {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
            Subir KMZ
          </button>
        </div>
      </div>

      {/* Layer tabs */}
      {layers.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="kmz-layer-tabs">
          {layers.map((l, i) => (
            <button
              key={l.id || i}
              data-testid={`kmz-tab-${i}`}
              onClick={() => setActiveIdx(i)}
              className={`text-xs font-mono uppercase tracking-wider px-3 py-1.5 rounded-xl border ${
                activeIdx === i
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
              }`}>
              {l.filename}
            </button>
          ))}
        </div>
      )}

      {/* Stats */}
      {active && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="kmz-stats">
          <Stat label="Placemarks" value={active.totals?.placemarks || 0} icon={MapPin} />
          <Stat label="Paths"      value={active.totals?.paths || 0}      icon={Route} />
          <Stat label="Polígonos"  value={active.totals?.polygons || 0}   icon={MapPin} />
          <Stat label="Total ft"   value={(active.totals?.total_path_ft || 0).toLocaleString()} accent="orange" icon={Route} />
        </div>
      )}

      {/* Map */}
      {active && allCoords.length > 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" style={{ height: 500 }} data-testid="kmz-map">
          <MapContainer center={center} zoom={13} scrollWheelZoom style={{ height: "100%", width: "100%" }}>
            <TileLayer
              attribution='&copy; OpenStreetMap'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <FitBounds bounds={allCoords} />
            {(active.placemarks || []).slice(0, 200).map((pm, i) => (
              <Marker key={`pm-${i}`} position={[pm.lat, pm.lon]}>
                <Popup>
                  <div className="text-xs font-mono">
                    <div className="font-semibold">{pm.name || `Node ${i+1}`}</div>
                    {pm.description && <div className="mt-1 text-slate-600 text-[11px]">{pm.description.slice(0,180)}</div>}
                    <div className="mt-1 text-slate-500">{pm.lat.toFixed(5)}, {pm.lon.toFixed(5)}</div>
                  </div>
                </Popup>
              </Marker>
            ))}
            {(active.paths || []).map((p, i) => {
              const positions = (p.coords || []).map(c => [c.lat, c.lon]);
              if (positions.length < 2) return null;
              return (
                <Polyline key={`path-${i}`} positions={positions}
                  pathOptions={{ color: PATH_COLORS[i % PATH_COLORS.length],
                                 weight: 3, opacity: 0.85 }}>
                  <Popup>
                    <div className="text-xs font-mono">
                      <div className="font-semibold">{p.name || `Path ${i+1}`}</div>
                      <div>{p.point_count} puntos · {p.distance_ft?.toLocaleString()} ft</div>
                    </div>
                  </Popup>
                </Polyline>
              );
            })}
          </MapContainer>
        </div>
      ) : !loading && layers.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-8 text-center text-sm text-slate-500" data-testid="kmz-empty">
          Sube un archivo .kmz o .kml de Google Earth para ver la red de fibra/datos sobre el mapa.
        </div>
      ) : null}
    </div>
  );
}

function Stat({ label, value, icon: Icon, accent = "blue" }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4 flex items-center justify-between">
      <div>
        <div className="text-xs font-mono uppercase tracking-wider text-slate-500">{label}</div>
        <div className="text-2xl font-mono font-semibold text-slate-900 mt-1">{value}</div>
      </div>
      <Icon size={16} className={accent === "orange" ? "text-[#EA580C]" : "text-[#003366]"} />
    </div>
  );
}
